import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { Check, X, Download, FileText } from "lucide-react";
import DataTable from "../ui/DataTable";
import { Act, Btn, Scrim, Modal } from "../ui/primitives";
import { ButtonSpinnerSlot } from "../ui/ButtonSpinner";
import useDemoActionGate from "../hooks/useDemoActionGate";
import DemoLockedDialog from "../ui/DemoLockedDialog";

/* ---- on-brand layout shell (matches the other list pages) ---- */
const PageShell = styled.div`
  min-height: calc(100vh - 32px);
  margin: 0 auto;
  padding: 0 28px 36px;
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};

  @media (max-width: 768px) {
    padding: 0 14px 28px;
  }
`;

const PageHero = styled.div`
  width: min(1180px, 100%);
  margin: ${({ theme }) => theme.space} auto ${({ theme }) => theme.gap};
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  h2 {
    margin: 0;
    color: ${({ theme }) => theme.text};
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -.01em;
  }
  p {
    margin: 2px 0 0;
    color: ${({ theme }) => theme.textMuted};
    font-size: ${({ theme }) => theme.fsSm};
  }
`;

const TableWrap = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
`;

const PdfLink = styled.a`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 11px 13px;
  margin-bottom: 8px;
  border-radius: ${({ theme }) => theme.rMd};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface2};
  color: ${({ theme }) => theme.text};
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  transition: background .15s, border-color .15s;

  &:hover {
    background: ${({ theme }) => theme.surface};
    border-color: ${({ theme }) => theme.borderStrong};
  }
  svg { color: ${({ theme }) => theme.accent}; flex: none; }
  &:last-child { margin-bottom: 0; }
`;

const Course = () => {
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [selectedRows, setSelectedRows] = useState([]);
  const [tableData, setTableData] = useState([]);
  const userType = localStorage.getItem("userType");
  const userId = localStorage.getItem("userId");
  const navigate = useNavigate();
  const [loadingState, setLoadingState] = useState({
    type: null,
    emails: [],
  });

  // For PDF Dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Handle expiration redirect
  useEffect(() => {
    const planExpire = localStorage.getItem("planExpire");
    if (planExpire === "1" && userType !== "2") {
      sessionStorage.removeItem('planRenewDismissed');
      navigate("/Portal/Instructor");
    }
  }, [navigate, userType]);

  // Basic Auth
  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  // ✅ Download all PDFs of a student as ZIP
  const handleDownloadAllPdfs = async (student) => {
    if (!guardAction('action.registration.downloadAttachments')) return;
    if (!student.pdfs || student.pdfs.length === 0) {
      alert("No PDFs found for this student!");
      return;
    }

    const zip = new JSZip();

    for (let i = 0; i < student.pdfs.length; i++) {
      const pdfUrl = student.pdfs[i].pdfUrl;
      try {
        const response = await fetch(pdfUrl);
        if (!response.ok) throw new Error("Network error: " + pdfUrl);
        const arrayBuffer = await response.arrayBuffer();
        zip.file(`PDF_${i + 1}.pdf`, arrayBuffer);
      } catch (error) {
        console.error("❌ Error downloading PDF:", pdfUrl, error);
      }
    }

    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, `${student.firstName}_${student.lastName}_PDFs.zip`);
    });
  };

  const getSelectedEmails = () => selectedRows.map((row) => row.email);

  // ✅ Common API handler (Reusable)
  const handleAction = async (type, email) => {
    const featureKey = type === 'approve' ? 'action.registration.approve' : 'action.registration.reject';
    if (!guardAction(featureKey)) return;
    let emails = email ? [email] : getSelectedEmails();

    if (!emails || emails.length === 0) {
      alert("Please select at least one student");
      return;
    }

    // ✅ set loading
    setLoadingState({ type, emails });

    try {
      const res = await fetch(`${apiUrl}/api/bulk-${type}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: getBasicAuthHeader(),
        },
        body: JSON.stringify({ emails }),
      });

      if (res.ok) {
        alert(`Students ${type === "approve" ? "Approved ✅" : "Rejected ❌"}`);
        fetchStudents();
      } else {
        // Surface the server's reason (e.g. 409 "<email> is already registered
        // for this course") instead of a generic message. Body may be JSON
        // ({ message }) or plain text depending on the endpoint.
        const raw = await res.text();
        let message = "Something went wrong. Please try again.";
        try {
          const data = JSON.parse(raw);
          message = data.message || data.error || message;
        } catch {
          if (raw) message = raw;
        }
        console.error(raw);
        alert(message);
        // The list may be stale (e.g. already-registered student) — refresh it.
        if (res.status === 409) fetchStudents();
      }
    } catch (err) {
      console.error(err);
      alert("Server error. Please try again.");
    } finally {
      // ✅ clear loading
      setLoadingState({ type: null, emails: [] });
    }
  };

  const fetchStudents = () => {
    fetch(apiUrl + `/api/register-students-with-pdf/${userId}`, {
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        Authorization: getBasicAuthHeader(),
      },
    })
      .then((resp) => resp.json())
      .then((resp) => {
        const normalized = resp.map((student) => ({
          ...student,
          pdfs: Array.isArray(student.pdfs)
            ? student.pdfs
            : student.pdfs
            ? [student.pdfs]
            : [],
        }));
        setTableData(normalized);
      })
      .catch((err) => console.error("Error fetching registrations:", err));
  };

  // ✅ Wrapper functions
  const handleBulkApprove = (email) => handleAction("approve", email);
  const handleBulkReject = (email) => handleAction("reject", email);

  // Fetch student registration data with PDFs
  useEffect(() => {
    fetchStudents();
  }, [apiUrl, userId]);

  const isBulkApproveLoading =
    loadingState.type === "approve" && loadingState.emails.length > 1;
  const isBulkRejectLoading =
    loadingState.type === "reject" && loadingState.emails.length > 1;

  return (
    <PageShell>
      <PageHero>
        <div>
          <h2>Registration requests</h2>
          <p>Review and approve students who registered for your courses.</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn onClick={() => handleBulkApprove()} disabled={loadingState.type !== null}>
            {isBulkApproveLoading ? (
              <ButtonSpinnerSlot size={16} />
            ) : (
              <><Check size={15} /> Approve selected</>
            )}
          </Btn>
          <Btn $variant="ghost" onClick={() => handleBulkReject()} disabled={loadingState.type !== null}>
            {isBulkRejectLoading ? (
              <ButtonSpinnerSlot size={16} />
            ) : (
              <><X size={15} /> Reject selected</>
            )}
          </Btn>
        </div>
      </PageHero>

      <TableWrap>
        <DataTable
          rows={tableData || []}
          rowKey={(r, i) => r.email ?? i}
          selectable
          onSelectionChange={setSelectedRows}
          search
          searchPlaceholder="Search registrations…"
          searchKeys={["firstName", "lastName", "email", "phone"]}
          primary={{
            header: "Participant",
            label: (r) => `${r.firstName || ""} ${r.lastName || ""}`.trim() || r.email,
            sub: (r) => r.email,
          }}
          columns={[
            { header: "Phone", render: (r) => r.phone, muted: true },
            { header: "Course", render: (r) => r.courseId, muted: true },
            {
              header: "Register Time",
              muted: true,
              mono: true,
              render: (r) => {
                if (!r.createdAt) return "";
                const date = new Date(r.createdAt);
                const day = String(date.getDate()).padStart(2, "0");
                const month = String(date.getMonth() + 1).padStart(2, "0");
                return `${day}-${month}-${date.getFullYear()}`;
              },
            },
          ]}
          actionsColumnWidth={260}
          actions={(r) => {
            const email = r.email;
            const pdfs = r.pdfs || [];
            const isLoading = loadingState.emails.includes(email);
            const isApproveLoading = isLoading && loadingState.type === "approve";
            const isRejectLoading = isLoading && loadingState.type === "reject";
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                {pdfs.length > 0 && (
                  <Btn
                    $variant="ghost"
                    $sm
                    disabled={loadingState.type !== null}
                    onClick={() => { setSelectedStudent(r); setOpenDialog(true); }}
                  >
                    <FileText size={14} /> {pdfs.length}
                  </Btn>
                )}
                <Btn $sm disabled={loadingState.type !== null} onClick={() => handleBulkApprove(email)}>
                  {isApproveLoading ? <ButtonSpinnerSlot size={14} /> : (<><Check size={14} /> Accept</>)}
                </Btn>
                <Btn $variant="ghost" $sm disabled={loadingState.type !== null} onClick={() => handleBulkReject(email)}>
                  {isRejectLoading ? <ButtonSpinnerSlot size={14} /> : (<><X size={14} /> Reject</>)}
                </Btn>
              </div>
            );
          }}
          emptyText="No registrations yet."
        />
      </TableWrap>

      {/* 📄 Attachments dialog */}
      {openDialog && selectedStudent && (
        <Scrim onClick={() => setOpenDialog(false)}>
          <Modal style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal__head">
              <div>
                <h3>Attachments</h3>
                <p>
                  {`${selectedStudent.firstName || ""} ${selectedStudent.lastName || ""}`.trim() ||
                    selectedStudent.email}
                </p>
              </div>
              <Act className="x-btn" title="Close" onClick={() => setOpenDialog(false)}>
                <X size={18} />
              </Act>
            </div>
            <div className="modal__body">
              {(selectedStudent.pdfs || []).map((pdf, index) => (
                <PdfLink
                  key={index}
                  href={pdf.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FileText size={15} /> PDF {index + 1}
                </PdfLink>
              ))}
            </div>
            <div className="modal__foot">
              <Btn $variant="ghost" onClick={() => setOpenDialog(false)}>Close</Btn>
              <Btn onClick={() => handleDownloadAllPdfs(selectedStudent)}>
                <Download size={15} /> Download all
              </Btn>
            </div>
          </Modal>
        </Scrim>
      )}
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </PageShell>
  );
};

export default Course;
